import { Injectable, Injector } from '@angular/core';
import * as L from 'leaflet';
import 'leaflet-editable';
import '@bazis/map/plugins/path-draggable';
import { BazisLPopupEvent, MapSettings } from '@bazis/map/models/map.models';
import ExtendedCanvas from '@bazis/map/plugins/extended-canvas';
import { BazisMapService } from '@bazis/map/services/map.service';
import { BehaviorSubject } from 'rxjs';
import { fromGeoJson } from '@bazis/map/services/map-utils';
import { DEFAULT_POLYGON_SETTINGS } from '@app/configuration.service';
import { TranslocoService } from '@jsverse/transloco';
import { LatLngTuple, MapOptions, Polygon } from 'leaflet';
import * as turf from '@turf/turf';
import { Feature } from '@turf/turf';
import * as geojson from 'geojson';

@Injectable()
export class EditableMapService extends BazisMapService {
    editLayer = new L.LayerGroup();

    featuresLayer = new L.LayerGroup();

    polygonOptions = {
        stroke: true,
        weight: 2,
        color: 'blue',
        fill: true,
        fillColor: 'blue',
        fillOpacity: 0.1,
        bubblingMouseEvents: true,
        interactive: true,
    };

    polylineOptions = {
        stroke: true,
        weight: 2,
        color: 'blue',
    };

    selectedPolylineOptions = {
        ...this.polylineOptions,
        color: 'red',
    };

    selectedPolygonOptions = {
        ...this.polygonOptions,
        fillColor: 'red',
        color: 'red',
    };

    editableTooltip;

    editableLayer$ = new BehaviorSubject([]);

    drawingShape = null;

    editableSelected = {
        polyline: [],
        polygon: [],
    };

    constructor(protected injector: Injector, private translocoService: TranslocoService) {
        super(injector);
    }

    initMap(domElement, options: MapSettings) {
        if (this.map) return;

        this.mapOptions = options;
        this.renderer = new ExtendedCanvas({ pane: 'overlayPane', tolerance: 0 });

        this.map = L.map(domElement, {
            center: options.defaultLocation,
            zoomControl: false,
            attributionControl: false,
            zoom: options.defaultZoom || 7,
            zoomMin: 0,
            zoomMax: 18,
            worldCopyJump: true,
            preferCanvas: true,
            editable: true,
            editOptions: {
                editLayer: this.editLayer,
                featuresLayer: this.featuresLayer,
                lineGuideOptions: {
                    color: '#00BC41',
                },
                skipMiddleMarkers: false,
            },
            renderer: this.renderer,
            ...options.additionalSettings,
        } as unknown as MapOptions);

        let tileLayerOptions = this.getInitialTileSettings(options);
        this.setTileLayer(tileLayerOptions);

        this.featuresLayer.addTo(this.map);
        this.editLayer.addTo(this.map);

        //this.map.createPane('overlayPane');

        this.map.on('popupclose', (e: BazisLPopupEvent) => {
            if (
                this.selected._ &&
                e?.popup?.options?.layerId === this.selected._.layerId &&
                this.selected._.object?.options?.properties?.id ===
                    e.popup.options.object.options.properties.id
            ) {
                this.setSelection(this.selected._.layerId, this.selected._.object, false);
            }
            this.popup = null;
        });
        this.map.on('popupopen', () => {
            //this.popup = null;
        });
        let prevZoom = this.map.getZoom();
        this.map.on('zoomend', (e) => {
            const currentZoom = this.map.getZoom();
            if (Math.floor(prevZoom) !== Math.floor(currentZoom)) {
                this.redrawZoomLayers();
            }
            prevZoom = currentZoom;
        });

        this.map.on('click', (e) => {
            if (this.selected._) {
                this.setSelection(this.selected._.layerId, this.selected._.object, false);
            }
            if (this._isDragging) return;
            Object.keys(this._mapLayers).forEach((layer) => {
                if (this._mapLayers[layer].isEditable) {
                    const ids = Object.keys(this._mapLayers[layer].leafletLayer._layers);
                    if (ids.length === 0) return;
                    const marker = this._mapLayers[layer].leafletLayer._layers[ids[0]];
                    marker.setLatLng(e.latlng);
                    marker.fire('drag');
                }
            });
        });

        this.map.on('editable:drawing:commit', (e) => {
            this.fireEvent(e.layer, this.drawingShape);

            this.drawingShape = null;
            this.updateCurrentEditableJson();
        });

        this.map.on('editable:dragend', (e) => {
            if (this.drawingShape) return;
            this.updateCurrentEditableJson();
        });

        this.map.on('editable:vertex:dragend', (e) => {
            console.log('dragend');
            if (this.drawingShape) return;
            this.updateCurrentEditableJson();
        });
        this.editableTooltip = L.tooltip({
            sticky: true,
            permanent: true,
            offset: [0, -40],
            className: 'leaflet-tooltip-editable',
            direction: 'center',
        }).setLatLng({
            lat: 0,
            lng: 0,
        });

        this.featuresLayer.on('click', this.processEditableClick, this);

        this.initEditableTooltip();
    }

    initEditableTooltip() {
        const addTooltip = (e) => {
            this.editableTooltip.setContent(
                `<div class="leaflet-tooltip__content">${this.translocoService.translate(
                    'map.tooltip.drawing.start',
                )}</div>`,
            );
            this.map.openTooltip(this.editableTooltip);
        };

        const removeTooltip = (e) => {
            this.map.closeTooltip(this.editableTooltip);
        };

        const updateTooltip = (e) => {
            if (e.layer.editor._drawnLatLngs.length < e.layer.editor.MIN_VERTEX) {
                this.editableTooltip.setContent(
                    `<div class="leaflet-tooltip__content">${this.translocoService.translate(
                        'map.tooltip.drawing.continue',
                    )}</div>`,
                );
            } else {
                this.editableTooltip.setContent(
                    `<div class="leaflet-tooltip__content">${this.translocoService.translate(
                        'map.tooltip.drawing.finish',
                    )}</div>`,
                );
            }
        };
        this.map.on('editable:drawing:start', addTooltip);
        this.map.on('editable:drawing:end', removeTooltip);
        this.map.on('editable:drawing:clicked', updateTooltip);
        this.map.on('mousemove', (e) => {
            this.editableTooltip.setLatLng(this.map.layerPointToLatLng(e.layerPoint));
        });
    }

    updateCurrentEditableJson() {
        const layers: L.Polygon[] = this.featuresLayer.getLayers() as L.Polygon[];
        if (layers.length === 0) return this.editableLayer$.next(null);
        this.editableLayer$.next(layers.map((layer) => layer.toGeoJSON().geometry));
    }

    setEditableLayerFromGeometry(boundaries) {
        if (!boundaries) return;

        const polygon = L.polygon(
            fromGeoJson(boundaries) as L.LatLngTuple[] | L.LatLngTuple[][],
            this.polygonOptions,
        );
        this.addPolygonToEditLayer(polygon);
    }

    setEditableLayerFromLatLng(boundaries: L.LatLng[]) {
        if (!boundaries) return;

        const polygon = L.polygon(boundaries, this.polygonOptions);
        this.addPolygonToEditLayer(polygon);
    }

    addPolygonToEditLayer(polygon: L.Polygon) {
        polygon.addTo(this.featuresLayer);
        polygon.enableEdit();
        this.map.editTools.stopDrawing();
        this.fireEvent(polygon);
    }

    fireEvent(layer: L.Polygon | L.Polyline, type = 'polygon') {
        layer.on('click', (e) => {
            this.featuresLayer.fire('click', {
                layerId: 'editable-' + type,
                object: this.featuresLayer.getLayer(e.target._leaflet_id),
                objectId: e.target._leaflet_id,
            });
        });
    }

    startPolygonDrawing() {
        this.map.editTools.startPolygon(null, this.polygonOptions);
        this.drawingShape = 'polygon';
    }

    startLineDrawing() {
        this.map.editTools.startPolyline(null, this.polylineOptions);
        this.drawingShape = 'polyline';
    }

    clearEditableLayer(emitEmptyEditable = false) {
        this.drawingShape = null;
        this.editLayer.clearLayers();
        this.featuresLayer.clearLayers();
        if (emitEmptyEditable) {
            this.editableLayer$.next([]);
        }
    }

    disableEditing() {
        if (!this.map) return;
        this.featuresLayer.getLayers().forEach((layer: L.Polygon) => {
            layer.disable();
        });
    }

    enableEditing() {
        if (!this.map) return;
        this.featuresLayer.getLayers().forEach((layer: L.Polygon) => {
            layer.enable();
        });
    }

    union() {
        const turfPolygons: turf.Feature<turf.Polygon>[] = this.editableSelected.polygon.map(
            (selected) => selected.object.toGeoJSON(),
        );
        const union = turf.union(...turfPolygons);

        for (let i = this.editableSelected.polygon.length - 1; i >= 0; i--) {
            this.editableSelected.polygon[i].object.remove();
            this.editableSelected.polygon.splice(i, 1);
        }
        this.editableSelected.polygon = [];

        const poly = L.polygon(fromGeoJson(union.geometry) as LatLngTuple[], {
            ...this.polygonOptions,
        });

        this.addPolygonToEditLayer(poly);
    }

    difference() {}

    intersect() {}

    split() {}

    cut() {}

    processEditableClick(e) {
        const getSelectedIndex = (layerId, source) =>
            this.editableSelected[source].findIndex((v) => v.objectId === layerId);

        if (e.layerId === 'editable-polygon') {
            const selectedIndex = getSelectedIndex(e.objectId, 'polygon');
            if (selectedIndex > -1) {
                e.object.setStyle(this.polygonOptions);
                this.editableSelected.polygon.splice(selectedIndex, 1);
            } else {
                e.object.setStyle(this.selectedPolygonOptions);
                this.editableSelected.polygon.push(e);
            }
        }

        if (e.layerId === 'editable-polyline') {
            const selectedIndex = getSelectedIndex(e.objectId, 'polyline');
            if (selectedIndex > -1) {
                e.object.setStyle(this.polylineOptions);
                this.editableSelected.polyline.splice(selectedIndex, 1);
            } else {
                e.object.setStyle(this.selectedPolylineOptions);
                this.editableSelected.polyline.push(e);
            }
        }
    }
}
